
import React, { useState, useEffect, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Send, Sparkles, Lightbulb, Clock, ShieldCheck, TrendingDown, TrendingUp, FileText } from "lucide-react";
import { toast } from "sonner";
import WorkflowMermaidDiagram from "@/components/WorkflowMermaidDiagram";
import { Module } from "@/components/ModuleList";
import { updateModuleNamesBasedOnInput, EnhancedModule, initialModules } from "@/data/modules";
import { Input } from "@/components/ui/input";
import { calculateSavingsMetrics } from "@/utils/savingsCalculations";


// Create inactive modules by setting all submodule active status to false
const inactiveModules = initialModules.map(module => ({
  ...module,
  subModules: module.subModules.map(subModule => ({
    ...subModule,
    active: false
  }))
}));

interface WorkflowCustomizerProps {
  initialModules: EnhancedModule[];
}

const WorkflowCustomizer: React.FC<WorkflowCustomizerProps> = ({ initialModules }) => {
  // Initialize with the provided modules, preserving active status
  const [modules, setModules] = useState<EnhancedModule[]>(initialModules);
  
  // Track if any modules were pre-selected via URL params
  const hasPreselectedModules = React.useMemo(() => {
    return initialModules.some(module => 
      module.subModules.some(subModule => subModule.active)
    );
  }, [initialModules]);
  const [userInput, setUserInput] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [isDevelopmentMode, setIsDevelopmentMode] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [savingsMetrics, setSavingsMetrics] = useState({
    timeReduction: 0,
    costReduction: 0,
    errorReduction: 0,
    timeSaved: 0,
    annualSavings: 0,
    annualCost: 0,
    roi: 0,
    totalActiveSubmodules: 0
  });
  const inputRef = useRef<HTMLInputElement>(null);

  // Check if running in development/localhost mode
  useEffect(() => {
    const hostname = window.location.hostname;
    const isDev = hostname === 'localhost' || hostname === '127.0.0.1' || hostname.includes('192.168.');
    setIsDevelopmentMode(isDev);
  }, []);

  // Load a random demo requirement


  // Process the user input using the sophisticated analysis from modules.ts
  const processInput = () => {
    if (!userInput.trim()) {
      toast.error("Please enter some requirements to analyze");
      return;
    }

    setIsProcessing(true);
    setHasUserInteracted(true);
    
    try {
      // Use the advanced analysis function from modules.ts
      const updatedModules = updateModuleNamesBasedOnInput(userInput, initialModules);
      
      // Update modules state
      setModules(updatedModules);
      
      // Show success toast
      const activeSubModulesCount = countActiveSubModules(updatedModules);
      
      if (activeSubModulesCount > 0) {
        toast.success(`Found ${activeSubModulesCount} matching processes for your requirements`, {
          description: "The workflow diagram has been updated with relevant modules.",
          duration: 5000
        });
        setShowReset(true);
      } else {
        toast.info("No matching processes found. Try describing your requirements with more details.", {
          duration: 5000
        });
      }
    } catch (error) {
      console.error("Error processing requirements:", error);
      toast.error("An error occurred while analyzing your requirements");
    } finally {
      setIsProcessing(false);
    }
  };

  const resetWorkflow = () => {
    setModules(inactiveModules);
    setUserInput("");
    setShowReset(false);
    setHasUserInteracted(false);
    toast.info("Workflow reset to default state");
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const countActiveSubModules = (modules: EnhancedModule[]): number => {
    return modules.reduce((count, module) => {
      return count + module.subModules.filter(sm => sm.active).length;
    }, 0);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserInput(e.target.value);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    processInput();
  };

  // Disabled: No longer auto-loading demo requirements
  // useEffect(() => {
  //   if (isDevelopmentMode && !userInput) {
  //     // 10% chance to pre-populate with a demo requirement
  //     const shouldLoadDemo = Math.random() < 0.1;
  //     if (shouldLoadDemo) {
  //       loadRandomDemo();
  //     }
  //   }
  // }, [isDevelopmentMode, userInput]);

  // Set hasUserInteracted to true if there are preselected modules
  useEffect(() => {
    if (hasPreselectedModules) {
      setHasUserInteracted(true);
      setShowReset(true);
    }
  }, [hasPreselectedModules]);

  // Focus input on component mount
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);
  
  // Calculate savings metrics whenever modules change
  useEffect(() => {
    const metrics = calculateSavingsMetrics(modules as EnhancedModule[]);
    setSavingsMetrics(metrics);
  }, [modules]);
  
  // Render the savings metric card
  const renderSavingsCard = (
    title: string, 
    value: string | number, 
    secondaryValue: string | number, 
    description: string,
    icon: React.ReactNode,
    bgColor: string,
    borderColor: string,
    textColor: string
  ) => {
    return (
      <div className={`bg-white p-3 rounded-lg shadow-sm border border-${borderColor} hover:shadow-md`}>
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className={`${bgColor} rounded-full p-1.5 ${textColor}`}>
              {icon}
            </div>
            <span className="text-gray-700 text-base font-medium">{title}</span>
          </div>
          <div className={`text-base font-semibold ${textColor}`}>{value}</div>
        </div>
        
        <div className="mt-1 flex justify-end">
          <div className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded-full">
            {secondaryValue}
          </div>
        </div>
        
        <p className="text-xs text-gray-600 mt-2">{description}</p>
      </div>
    );
  };

  return (
    <div className="space-y-8">
      {/* Content with constrained width */}
      <Card className="p-6 border-none">
        <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold tracking-tight text-gray-900 mb-4">
          Workflow Customizer <br/>
          <span id="workflow-text"
                className="bg-gradient-to-r from-green-300 to-green-600 bg-clip-text text-transparent mt-2 pe-2">Traditional vs AI-Enhanced</span>
        </h1>




              {/* Savings metrics container - contained width */}
      <div className="mt-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {hasUserInteracted && savingsMetrics.totalActiveSubmodules > 0 ? (
            // Show actual metrics when user has interacted and there are active modules
            <>
              {renderSavingsCard(
                "Time",
                `${savingsMetrics.timeReduction}%`,
                `${savingsMetrics.timeSaved}h/week`,
                `Our AI automation reduces time spent on compliance tasks by ${savingsMetrics.timeReduction}%, saving your team ${savingsMetrics.timeSaved} hours every week.`,
                <Clock className="h-4 w-4" />,
                "bg-green-50",
                "green-200",
                "text-green-600"
              )}
              
              {renderSavingsCard(
                "Cost",
                `${savingsMetrics.costReduction}%`,
                `€${Math.round(savingsMetrics.annualSavings / 1000)}k/yr`,
                `Reduce compliance-related costs by ${savingsMetrics.costReduction}%, saving approximately €${savingsMetrics.annualSavings.toLocaleString()} annually.`,
                <TrendingDown className="h-4 w-4" />,
                "bg-blue-50",
                "blue-200",
                "text-blue-600"
              )}
              
              {renderSavingsCard(
                "Quality",
                `${savingsMetrics.errorReduction}%`,
                "Higher compliance",
                `Reduce compliance errors and violations by ${savingsMetrics.errorReduction}% through automated validation and consistency checks.`,
                <ShieldCheck className="h-4 w-4" />,
                "bg-purple-50",
                "purple-200",
                "text-purple-600"
              )}
              
              {renderSavingsCard(
                "ROI",
                `${savingsMetrics.roi}%`,
                `€${Math.round(savingsMetrics.annualCost * (savingsMetrics.roi / 100)).toLocaleString()} return`,
                `Return on investment of ${savingsMetrics.roi}% means every euro invested returns €${(savingsMetrics.roi / 100).toFixed(2)} in value annually.`,
                <TrendingUp className="h-4 w-4" />,
                "bg-indigo-50",
                "indigo-200",
                "text-indigo-600"
              )}
            </>
          ) : (
            // Show placeholder metrics when user hasn't interacted or there are no active modules
            <>
              {renderSavingsCard(
                "Time",
                "-",
                "h/week",
                "Enter your compliance requirements above to see how our AI automation can reduce time spent on compliance tasks.",
                <Clock className="h-4 w-4" />,
                "bg-gray-50",
                "gray-200",
                "text-gray-500"
              )}
              
              {renderSavingsCard(
                "Cost",
                "-",
                "€k/yr",
                "Analyze your requirements to see potential cost savings for your compliance-related activities.",
                <TrendingDown className="h-4 w-4" />,
                "bg-gray-50",
                "gray-200",
                "text-gray-500"
              )}
              
              {renderSavingsCard(
                "Quality",
                "-",
                "Improved accuracy",
                "Discover how our AI-enhanced processes can reduce compliance errors and improve overall quality.",
                <ShieldCheck className="h-4 w-4" />,
                "bg-gray-50",
                "gray-200",
                "text-gray-500"
              )}
              
              {renderSavingsCard(
                "ROI",
                "-",
                "Expected return",
                "Calculate the return on investment you can expect from implementing our AI compliance solutions.",
                <TrendingUp className="h-4 w-4" />,
                "bg-gray-50",
                "gray-200",
                "text-gray-500"
              )}
            </>
          )}
        </div>
      </div>

      </Card>
      

      {/* Full width diagram container */}
      <div className="w-screen relative left-1/2 right-1/2 -mx-[50vw]">
        {hasUserInteracted ? (
          <div className="h-[850px] bg-gray-50 shadow-inner">
            <WorkflowMermaidDiagram modules={modules} />
          </div>
        ) : (
          <div className="h-[850px] bg-gray-50 shadow-inner flex flex-col items-center justify-center p-8">
            <div className="max-w-2xl text-center">


              <div className="text-gray-400 border border-gray-200 rounded-lg p-6 bg-white/50">
                <p className="italic">The diagram will display both traditional and AI-augmented compliance processes, 
                with step-by-step visualizations of how each process is transformed.</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkflowCustomizer;
